import { useSocket } from '@elotech/components';
import React, { useEffect, useMemo, useState } from 'react';

import { ProgressService } from '../../service';
import { Progress } from '../../type';
import { ProgressStatus } from '../../type/enum';

type Props = {
  baseResource?: string;
  children?: React.ReactNode;
};

export const ProgressContext = React.createContext<{
  count?: number;
  setModalVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  modalVisible?: boolean;
  progresses?: Record<string, Progress>;
  descartarProgresso?: (id: string) => void;
}>({});

export const ProgressContainer = ({ baseResource, children }: Props) => {
  const [topic, setTopic] = useState<string>();
  const [processing, setProcessing] = useState<Progress[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [progresses, setProgresses] = useState<Record<string, Progress>>({});
  const socket = useSocket();

  const progressService = useMemo(
    () => new ProgressService(baseResource),
    [baseResource]
  );

  // Processa o recebimento
  useEffect(() => {
    if (!processing.length) {
      return;
    }

    setProcessing(old => {
      const progress = old.shift();
      if (!progress) {
        return old;
      }
      setProgresses(old => {
        const newProgresses = { ...old };
        newProgresses[progress.id] = progress;
        if (progress.status === ProgressStatus.SUCCEED) {
          sendRemoveProgress(progress.id);
        }
        if (progress.status === ProgressStatus.FAILED) {
          // eslint-disable-next-line no-console
          console.warn('Progresso falhado', progress.title, progress.message);
        }
        return newProgresses;
      });

      return old;
    });
  }, [processing]);

  // Enfileira o progresso para ser processado
  const handleMessage = (progressTmp: any) => {
    let progress: Progress;
    try {
      progress = progressTmp?.id ? progressTmp : JSON.parse(progressTmp[0]);
    } catch (e) {
      window.console.warn('Falha ao desserializar progresso', e);
      return;
    }

    setProcessing(old => [...old, progress]);
  };

  const descartarProgresso = (id: string) => {
    doRemoveProgress(id);
    progressService?.deleteProgress(id);
  };

  const sendRemoveProgress = (id: string) => {
    setTimeout(() => {
      doRemoveProgress(id);
      progressService?.deleteProgress(id);
    }, 2000);
  };

  const doRemoveProgress = (id: string) => {
    setProgresses(old => {
      const newProgresses = { ...old };
      delete newProgresses[id];
      if (!Object.keys(newProgresses).length) {
        setModalVisible(false);
      }
      return newProgresses;
    });
  };

  const receiveRemoveProgress = ([payload]) => {
    const id = JSON.parse(payload);
    doRemoveProgress(id);
  };

  // Trata de (des)registrar o listener no socket
  useEffect(() => {
    if (!topic || !socket) {
      return;
    }
    socket.on(topic, handleMessage);
    socket.on(`${topic}-progress-removed`, receiveRemoveProgress);

    return () => {
      socket.off(topic, handleMessage);
      socket.off(`${topic}-progress-removed`, receiveRemoveProgress);
    };
  }, [socket, topic]);

  // Busca configuração e progressos anteriores
  useEffect(() => {
    if (!progressService || !socket) {
      return;
    }

    Promise.all([
      progressService.getTopic(),
      progressService.getProgresses()
    ]).then(([{ data: topic }, { data: newProgressList }]) => {
      if (!topic) {
        return;
      }
      setTopic(topic);
      newProgressList.forEach(progress => handleMessage(progress));
    });

    return () => {
      setTopic(undefined);
      setProgresses({});
      setProcessing([]);
      setModalVisible(false);
    };
  }, [progressService, socket]);

  return (
    <ProgressContext.Provider
      value={{
        count: Object.keys(progresses).length,
        setModalVisible,
        progresses,
        modalVisible,
        descartarProgresso
      }}
    >
      {children}
    </ProgressContext.Provider>
  );
};
